import { captureException } from '@/lib/errorReporting';
import { useUIStore } from '@/store/uiStore';
import { getMountConfig } from '@/mountConfig';
import axios from 'axios';

// Extend Window for the 401 redirect guard flag
declare global {
  interface Window {
    __reloading401?: boolean;
  }
}

// Extend axios config to support silent flag — avoids sending a custom header
// which would trigger a CORS preflight on cross-origin API servers.
//
// `silent401`  suppresses ONLY the 401 redirect-to-login.
// `silent`     suppresses auth/server UI handling for requests where the caller
//              treats failures as an expected branch (e.g. internal session
//              checks or best-effort searches that degrade to "no results").
// `silent5xx`  suppresses ONLY the 5xx "server error" toast (the error is still
//              reported) — for background status checks whose caller shows
//              nothing on failure.
// `skipGlobalLoader` keeps the request out of the universal AngularJS spinner —
//              ONLY for background polling whose caller renders its own
//              progress (e.g. the Dispute Agent panel polls for minutes; the
//              full-page spinner would flash on every poll).
declare module 'axios' {
  export interface AxiosRequestConfig {
    silent401?: boolean;
    silent?: boolean;
    silent5xx?: boolean;
    skipGlobalLoader?: boolean;
  }
  export interface InternalAxiosRequestConfig {
    silent401?: boolean;
    silent?: boolean;
    silent5xx?: boolean;
    skipGlobalLoader?: boolean;
  }
}

export const axiosClient = axios.create({
  withCredentials: true,
  // Match AngularJS: no aggressive timeout - allow slow API responses
  timeout: 120000, // 2 minutes
  maxContentLength: 50 * 1024 * 1024, // 50 MB — prevents memory exhaustion from oversized responses
  maxBodyLength: 50 * 1024 * 1024, // 50 MB — limits outbound payload size
});

// Track active requests for loading state
let activeRequests = 0;

axiosClient.interceptors.request.use((config) => {
  // Ensure Accept header matches AngularJS for consistent responses
  if (!config.headers.Accept) {
    config.headers.Accept = 'application/json, text/plain, */*';
  }

  if (config.skipGlobalLoader === true) {
    return config;
  }

  // Safety: reset counter if it drifted negative (should never happen)
  if (activeRequests < 0) {
    activeRequests = 0;
  }

  // Show loader on every request start — matches AngularJS $http interceptor
  // which broadcasts _START_REQUEST_ on every request, not just the first
  activeRequests++;
  useUIStore.getState().startLoading();

  return config;
});

axiosClient.interceptors.response.use(
  (response) => {
    if (response.config?.skipGlobalLoader === true) {
      return response;
    }
    // Hide loader when all requests complete
    activeRequests = Math.max(0, activeRequests - 1);
    if (activeRequests === 0) {
      useUIStore.getState().stopLoading();
    }
    return response;
  },
  async (error) => {
    // Hide loader on error (requests that never started it must not decrement)
    if (error?.config?.skipGlobalLoader !== true) {
      activeRequests = Math.max(0, activeRequests - 1);
      if (activeRequests === 0) {
        useUIStore.getState().stopLoading();
      }
    }

    const status = error?.response?.status;

    if (status === 401) {
      // Skip session-expiry handling for requests marked as silent (non-critical API calls)
      const isSilent = error?.config?.silent401 === true || error?.config?.silent === true;

      // VendorConnect has its own Auth0/PKCE flow handled by VendorAuthGuard +
      // vcAxiosClient. Mirrors the AngularJS auth interceptor
      // (public/SPIGlassApp/app.js lines 28-43) which bailed out of the Login
      // redirect for /VendorConnect/* paths and when vc_auth_pending=vendor.
      // Without this, a stray staff-flavored API call (e.g. a chrome component
      // that briefly mounted before the vendor context took over) would 401,
      // trip the re-auth flow, and disrupt a fully-authenticated vendor right
      // after a successful PKCE callback.
      //
      // The querystring check below also mirrors legacy app.js:34-37: while an
      // Auth0 PKCE callback (`?code=...&state=...`) is mid-resolution, the
      // browser still has the OAuth params in the URL and any 401 that fires
      // before VendorAuthGuard finishes the token exchange should NOT trigger
      // the staff Windows Auth bounce.
      const hash = (window.location.hash || '').toLowerCase();
      const qs = window.location.search || '';
      const isPkceCallback = qs.indexOf('code=') > -1 && qs.indexOf('state=') > -1;
      const isVendorContext =
        isPkceCallback ||
        hash.includes('vendorconnect') ||
        hash.includes('vendor-connect') ||
        (() => {
          try {
            return sessionStorage.getItem('vc_auth_pending') === 'vendor';
          } catch {
            return false;
          }
        })();

      const { mode } = getMountConfig();

      // A 401 from the WorkOrderInfo lookup means the work order is wrong or
      // inaccessible — NOT that the session is dead (the backend uses 401 for
      // "no such / not-permitted work order"). Bouncing through Windows Auth
      // here just lands back on the same bad work-order URL and 401s again, so
      // skip the bounce entirely (zero bounces): send the user straight to
      // /Home/dashboard.
      const requestUrl = String(error?.config?.url || '');
      const isWorkOrderInfo401 = requestUrl.includes('/API/WorkOrderInfo');
      if (!isSilent && !isVendorContext && mode === 'standalone' && isWorkOrderInfo401) {
        useUIStore.getState().showWarning('Unable to load that work order.');
        window.location.hash = '#/Home/dashboard';
        return Promise.reject(error);
      }

      if (!isSilent && !isVendorContext && mode === 'standalone') {
        // The Windows Auth cookie expired. Instead of dumping the user on
        // /login, bounce through Windows Auth to mint a fresh cookie — a
        // full-page navigation, the only reliable way to renew an
        // NTLM/Negotiate session. beginReauth() captures the current route so
        // AuthGate restores it after the bounce-back. If it CAN'T recover
        // (loop guard tripped after repeated failures, or pure localhost), it
        // resolves false and we hand off to handleSessionExpired() → /login so
        // the user isn't trapped in a redirect loop.
        const { beginReauth } = await import('@/lib/reauth');
        const navigating = await beginReauth();
        if (!navigating) {
          const { useAuthStore } = await import('@/store/authStore');
          useAuthStore.getState().handleSessionExpired();
        }
        // navigating === true → the page is leaving; nothing more to do.
      } else if (!isSilent && !isVendorContext && mode !== 'standalone' && !window.__reloading401) {
        // Embedded mode: AngularJS owns auth — reload so it handles the redirect.
        window.__reloading401 = true;
        useUIStore.getState().showWarning('Session expired. Please log in again.');
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      }
    } else if (status === 403) {
      // Silent calls (e.g. GetPermissions session check) handle 403 internally
      // and should not surface a toast — the caller decides what to do.
      if (error?.config?.silent !== true) {
        useUIStore.getState().showError('You do not have permission to perform this action.');
      }
    } else if (status != null && status >= 500) {
      if (error?.config?.silent5xx !== true) {
        useUIStore
          .getState()
          .showError('A server error occurred. Please try again or contact support.');
      }
      captureException(error instanceof Error ? error : new Error(String(error)), {
        url: error?.config?.url,
        status,
      });
    } else if (!error?.response) {
      // No response at all — network error, timeout, CORS failure, or request aborted
      if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
        useUIStore
          .getState()
          .showError('The request timed out. Please check your connection and try again.');
      } else if (error?.code === 'ERR_CANCELED') {
        // Request was deliberately cancelled (AbortController) — no user notification needed
      }
    }

    return Promise.reject(error);
  }
);
